/**
 * What a server render knows about how long its own content stays true
 * (VIP-12277).
 *
 * The HTTP layer writes the cache directives, but only the page knows what it
 * rendered: a special price that ends in forty seconds, a product shown as
 * sold out, which product the markup is about. Loaders report those facts
 * here, `entry-server` hands them over as `RenderResult.cacheHints`, and the
 * HTTP layer turns them into a capped TTL and a `Cache-Tag` header. Facts, not
 * directives: the numbers — how briefly a sold-out page lives, when stale
 * serving is still allowed — stay in one place, next to the header they end
 * up in.
 *
 * A sink rather than a stored value, for the same reason `ssrDataStatus` is
 * one: concurrent renders must not see each other's hints, so the server
 * entry routes every report into its own AsyncLocalStorage context.
 */

export interface SsrCacheHints {
  /**
   * Epoch milliseconds of the earliest moment the rendered content stops
   * being true on its own: a special price starting or ending, the price's
   * validity running out. Nothing writes to the product when that happens, so
   * no purge fires — the TTL is the only thing that keeps a cache from
   * serving the old price past it.
   */
  changesAt?: number
  /**
   * The page shows its product as not purchasable. A false sold-out is the
   * page state where being wrong costs the most — a customer who would have
   * bought leaves — so it is re-checked often, as platform-api-v2 does for
   * the product itself (VIP-12119).
   */
  unavailable?: boolean
  /** Products whose data the markup renders: the handle a purge uses. */
  productIds?: number[]
}

type HintSink = (hints: SsrCacheHints) => void

let _sink: HintSink | null = null

/** Installed by the server entry; resets with `null` (tests). */
export const setSsrCacheHintSink = (sink: HintSink | null): void => {
  _sink = sink
}

/**
 * Report facts about the current render's content. No-op in the browser and
 * outside a render, so loaders can call it unconditionally.
 */
export const reportSsrCacheHints = (hints: SsrCacheHints): void => {
  _sink?.(hints)
}

/**
 * The earliest of `moments` that lies after `now`, in epoch milliseconds.
 * Moments already past are skipped: a special price that has started says
 * nothing about when the page changes next. Takes the ISO strings the price
 * object carries; null, undefined and unparsable values are skipped too.
 */
export const nextChangeAfter = (
  now: number,
  moments: ReadonlyArray<string | null | undefined>
): number | undefined => {
  let next: number | undefined
  for (const moment of moments) {
    if (moment == undefined) continue
    const at = Date.parse(moment)
    if (Number.isNaN(at) || at <= now) continue
    if (next == undefined || at < next) next = at
  }
  return next
}

/**
 * Two reports from the same render, combined: the earliest change wins, one
 * product shown as sold out makes the page count as sold out, and every
 * reported product stays purgeable.
 */
export const mergeSsrCacheHints = (
  a: SsrCacheHints,
  b: SsrCacheHints
): SsrCacheHints => {
  const changes = [a.changesAt, b.changesAt].filter(
    (at): at is number => at != undefined
  )
  const productIds = [
    ...new Set([...(a.productIds ?? []), ...(b.productIds ?? [])])
  ]
  return {
    ...(changes.length > 0 && { changesAt: Math.min(...changes) }),
    ...((a.unavailable || b.unavailable) && { unavailable: true }),
    ...(productIds.length > 0 && { productIds })
  }
}
